import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** emerald = positive, rose = destructive */
  tone?: 'emerald' | 'rose' | 'amber';
  busy?: boolean;
  /** Disables the confirm button independently of `busy` — e.g. a required field not yet filled. */
  confirmDisabled?: boolean;
}

/**
 * ConfirmModal — custom replacement for window.confirm().
 *
 * Per feedback_no_system_alerts: the platform forbids window.alert /
 * window.confirm / window.prompt. All confirmations must be rendered as
 * real React modals with full dark-theme styling + 44px touch targets.
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  tone = 'emerald',
  busy = false,
  confirmDisabled = false,
}) => {
  if (!open) return null;

  const toneClasses: Record<string, { iconBg: string; iconColor: string; btn: string }> = {
    emerald: {
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      btn: 'bg-emerald-600 text-white hover:bg-emerald-700',
    },
    rose: {
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      btn: 'bg-rose-600 text-white hover:bg-rose-700',
    },
    amber: {
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      btn: 'bg-amber-600 text-white hover:bg-amber-700',
    },
  };
  const t = toneClasses[tone];

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-11 h-11 ${t.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <AlertTriangle size={22} className={t.iconColor} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-stone-900">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-stone-400 hover:text-stone-600 p-1 rounded-md min-w-[32px] min-h-[32px]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="text-sm text-stone-600 mb-5">{message}</div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 disabled:opacity-50 min-h-[44px]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            className={`flex-1 px-4 py-3 font-bold rounded-xl disabled:opacity-50 min-h-[44px] ${t.btn}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
